import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const client = axios.create({ withCredentials: true });

const inputCls = "w-full bg-cream border border-sand rounded-sm px-4 py-3 text-sm text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-coral transition-colors duration-300";

const TABS = [
  ["contacts", "Inquiries"],
  ["seminar_registrations", "Seminar Registrations"],
  ["orders", "Store Orders"],
];

function Table({ rows, columns }) {
  if (!rows.length) return <p className="text-sm text-charcoal/60 py-10 text-center">Nothing here yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-sand">
            {columns.map(([k, label]) => (
              <th key={k} className="text-left py-3 pr-6 font-mono text-[11px] tracking-widest text-charcoal/50 uppercase whitespace-nowrap">{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-sand/60 align-top">
              {columns.map(([k]) => (
                <td key={k} className="py-3 pr-6 text-charcoal/80 max-w-xs">
                  <span className="line-clamp-3">{String(r[k] ?? "—")}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("contacts");

  const load = async () => {
    try {
      const { data } = await client.get(`${API}/admin/submissions`);
      setData(data);
    } catch {
      toast.error("Could not load submissions.");
    }
  };

  useEffect(() => {
    client.get(`${API}/auth/me`)
      .then((r) => { setUser(r.data); load(); })
      .catch(() => setUser(false))
      .finally(() => setChecking(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await client.post(`${API}/auth/login`, { email, password });
      setUser(data);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Login failed. Please try again.");
    }
  };

  const logout = async () => {
    await client.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
    setData(null);
  };

  if (checking) {
    return <div className="bg-cream min-h-screen pt-40 text-center text-sm text-charcoal/60">Checking session…</div>;
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="bg-cream min-h-screen pt-16 flex items-center justify-center px-6">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm bg-cream-surface border border-sand rounded-sm p-10">
          <p className="font-serif text-2xl text-navy text-center">架け橋</p>
          <p className="mt-2 text-center text-xs font-mono tracking-[0.2em] text-charcoal/50 uppercase">Admin sign in</p>
          <div className="mt-8 space-y-4">
            <input data-testid="admin-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <input data-testid="admin-password" type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
            {error && <p data-testid="admin-login-error" className="text-sm text-coral-dark">{error}</p>}
            <button type="submit" data-testid="admin-login-submit" className="w-full bg-navy hover:bg-navy-surface text-cream-surface font-medium py-3 rounded-full transition-colors duration-300">
              Sign in
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="bg-cream min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-14">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-mono tracking-[0.2em] text-coral-dark uppercase">Admin</p>
            <h1 className="mt-2 font-serif text-3xl text-navy">Submissions</h1>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm border border-sand hover:border-navy px-4 py-2 rounded-full transition-colors duration-300">
            <LogOut size={14} /> Sign out
          </button>
        </div>
        <div className="mt-8 flex gap-2 flex-wrap">
          {TABS.map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`text-xs font-mono tracking-wider px-4 py-2 rounded-full border transition-colors duration-300 ${
                tab === key ? "bg-navy text-cream-surface border-navy" : "border-sand text-charcoal/70 hover:border-navy"
              }`}
            >
              {label}{data ? ` (${data[key].length})` : ""}
            </button>
          ))}
        </div>
        <div className="mt-6 bg-cream-surface border border-sand rounded-sm p-6 sm:p-8" data-testid="admin-table">
          {!data ? (
            <p className="text-sm text-charcoal/60 py-10 text-center">Loading…</p>
          ) : tab === "contacts" ? (
            <Table rows={data.contacts} columns={[["created_at", "Date"], ["name", "Name"], ["email", "Email"], ["company", "Company"], ["direction", "Direction"], ["inquiry_type", "Type"], ["message", "Message"]]} />
          ) : tab === "seminar_registrations" ? (
            <Table rows={data.seminar_registrations} columns={[["created_at", "Date"], ["name", "Name"], ["email", "Email"], ["company", "Company"], ["seminar_title", "Seminar"], ["seminar_date", "Seminar date"], ["attendees", "Attendees"]]} />
          ) : (
            <Table rows={data.orders} columns={[["created_at", "Date"], ["lookup_key", "Product"], ["amount", "Amount (¢)"], ["currency", "Currency"], ["payment_status", "Payment"]]} />
          )}
        </div>
      </div>
    </div>
  );
}
